// components/utils/InputFileUpload.tsx
import { useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

export interface IDocumento {
  name: string;
  type: string;
  size: number; 
  lastModified: number;
  extension?: string;
  contentBase64: string; 
}

type MaxByType = {
  image?: number; 
  docx?: number;  
  pdf?: number;  
};

type Props = {
  /**
   * Límite de tamaño global en MB (tope). Por defecto 15MB.
   * Si existe y es menor al límite por tipo, se aplica el menor.
   */
  maxSizeMB?: number;

  /**
   * Callback al terminar de cargar el archivo (exitoso).
   */
  onLoaded: (doc: IDocumento) => void;

  /**
   * Lista de tipos/extensiones aceptadas para el input "accept".
   * Por defecto: .doc,.docx,.pdf,.png,.jpg,.jpeg
   */
  accept?: string | undefined;

  /**
   * Variante visual del botón MUI. Por defecto 'contained'.
   */
  variant?: "text" | "outlined" | "contained";

  /**
   * Texto del botón.
   */
  label?: string;

  /**
   * Deshabilitar el botón.
   */
  disabled?: boolean;

  /**
   * Límite por tipo (MB). Si no se pasa, usa los recomendados.
   */
  maxByTypeMB?: MaxByType;

  /**
   * Título del modal de error (opcional).
   */
  errorTitle?: string;
};

const DEFAULT_ACCEPT = [".doc", ".docx", ".pdf", ".png", ".jpg", ".jpeg"].join(",");


const DEFAULT_LIMITS: Required<MaxByType> = {
  image: 4,  
  docx: 2,   
  pdf: 2,  
};

const ALLOWED_EXTS = new Set(["doc", "docx", "pdf", "png", "jpg", "jpeg"]);

function getExtension(name: string): string | undefined {
  const ext = name.split(".").pop();
  return ext ? ext.toLowerCase() : undefined;
}

function detectCategory(mime: string, name: string): keyof MaxByType | "invalid" {
  const ext = getExtension(name);

  // Bloquear cualquier cosa fuera de las permitidas explícitas
  if (!ext || !ALLOWED_EXTS.has(ext)) return "invalid";

  // Categorías
  if (ext === "pdf" || mime === "application/pdf") return "pdf";
  if (
    ext === "docx" || ext === "doc" ||
    mime === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime === "application/msword"
  ) return "docx";
  if (
    ext === "png" || ext === "jpg" || ext === "jpeg" ||
    mime.startsWith("image/")
  ) return "image";

  return "invalid";
}

function getBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // Usamos readAsDataURL y luego quitamos el prefijo "data:...;base64,"
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

const FileUpload: React.FC<Props> = ({
  maxSizeMB = 15,
  onLoaded,
  accept = DEFAULT_ACCEPT,
  variant = "contained",
  label = "Adjuntar archivo",
  disabled,
  maxByTypeMB,
  errorTitle = "No se puede cargar el archivo",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState(false);

  // Modal de error
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const openError = (msg: string) => {
    setErrorMessage(msg);
    setErrorOpen(true);
  };

  const limits = {
    ...DEFAULT_LIMITS,
    ...(maxByTypeMB || {}),
  };

  const handleClick = () => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo/extension
    const category = detectCategory(file.type, file.name);
    if (category === "invalid") {
      openError("Tipo de archivo no permitido. Solo se admiten: DOC, DOCX, PDF, PNG, JPG/JPEG.");
      e.target.value = ""; // reset para poder re-seleccionar
      return;
    }

    // Validar tamaño por tipo + tope global
    const sizeMB = file.size / (1024 * 1024);
    const perTypeLimit = limits[category] ?? DEFAULT_LIMITS.image; // fallback seguro
    const effectiveLimit = Math.min(perTypeLimit, maxSizeMB);

    if (sizeMB > effectiveLimit) {
      const labelCat =
        category === "image" ? "imágenes (PNG/JPG/JPEG)" :
          category === "docx" ? "documentos DOC/DOCX" :
            "PDF";
      openError(
        `El archivo supera el límite permitido para ${labelCat}: ${effectiveLimit} MB.\n` +
        `Tamaño del archivo: ${sizeMB.toFixed(2)} MB.`
      );
      e.target.value = ""; // reset
      return;
    }

    try {
      setLoading(true);
      const base64 = await getBase64(file);
      const extension = getExtension(file.name);

      onLoaded({
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        extension,
        contentBase64: base64,
      });
    } catch (err) {
      console.error("Error al leer archivo", err);
      openError("No se pudo leer el archivo.");
    } finally {
      setLoading(false);
      // Permite volver a seleccionar el mismo archivo si se desea
      e.target.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept} 
        style={{ display: "none" }}
        onChange={handleChange}
      />

      <Tooltip
        placement="right"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#1D3D4D",
              color: "#FFF",
              fontFamily: "Montserrat",
              fontSize: "0.675rem",
              padding: "8px 12px",
              borderRadius: "8px",
            },
          },
          arrow: {
            sx: {
              color: "#1D3D4D",
            },
          },
        }}
        title="Formatos: DOC/DOCX (2MB), PDF(2MB), PNG(4MB), JPG/JPEG (4MB)">
        <span>
          <Button
            onClick={handleClick}
            variant={variant}
            startIcon={loading ? <CircularProgress size={18} /> : <CloudUploadIcon />}
            disabled={disabled || loading}
            sx={{ whiteSpace: "nowrap", textTransform: "none", borderRadius: 3 }}
          >
            {label}
          </Button>
        </span>
      </Tooltip>

      {/* Modal de error */}
      <Dialog open={errorOpen} onClose={() => setErrorOpen(false)} maxWidth="xs" fullWidth sx={{fontFamily: "Montserrat"}}>
        <DialogTitle sx={{color: " #1D3D4D", fontFamily: "Montserrat"}}>{errorTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {errorMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button sx={{color: " #1D3D4D", fontFamily: "Montserrat"}}  onClick={() => setErrorOpen(false)} autoFocus>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUpload;
